/**
 * Product gallery (HopHopShop editorial layout).
 *
 * Desktop: all images visible at once in an adaptive collage grid (1-5 images).
 * Mobile: horizontal scroll-snap carousel with dot pagination + counter.
 *
 * - Listens for `product:variant-changed` to scroll/highlight the matching image.
 * - Tracks scroll position on mobile to update the active dot and "01 / N" counter.
 * - Dot clicks scroll the matching slide into view.
 */
import { Component } from "@theme/component";

class ProductGallery extends Component {
  setup() {
    this.slides = this.$$("[data-image-slide]");
    if (this.slides.length === 0) return;

    this.scroller = this.$("[data-main-image]");
    this.dots = this.$$("[data-gallery-dot]");
    this.counterEl = this.$("[data-gallery-current]");

    document.addEventListener("product:variant-changed", (e) =>
      this.#syncToVariant(e.detail?.variant),
    );

    this.dots.forEach((dot) => {
      dot.addEventListener("click", () =>
        this.#goTo(Number(dot.dataset.index)),
      );
    });

    if (this.scroller) {
      this.scroller.addEventListener("scroll", () => this.#onScroll(), {
        passive: true,
      });
    }

    this.#bindZoom();
  }

  #bindZoom() {
    const cell = this.$("[data-image-zoom]");
    if (!cell) return;
    let rafId = 0;
    let px = 50;
    let py = 50;

    const commit = () => {
      cell.style.setProperty("--zoom-x", `${px.toFixed(1)}%`);
      cell.style.setProperty("--zoom-y", `${py.toFixed(1)}%`);
      rafId = 0;
    };

    cell.addEventListener("pointermove", (e) => {
      if (e.pointerType !== "mouse") return;
      const rect = cell.getBoundingClientRect();
      px = ((e.clientX - rect.left) / rect.width) * 100;
      py = ((e.clientY - rect.top) / rect.height) * 100;
      if (!rafId) rafId = requestAnimationFrame(commit);
    });
  }

  #goTo(index) {
    const target = this.slides[index];
    if (!target) return;
    target.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
    this.#setActive(index);
  }

  #onScroll() {
    if (!this.scroller) return;
    // Use rAF to coalesce scroll bursts.
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      const center = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
      let bestIndex = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(slideCenter - center);
        if (dist < bestDistance) {
          bestDistance = dist;
          bestIndex = i;
        }
      });
      this.#setActive(bestIndex);
    });
  }

  #setActive(index) {
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle("is-active", isActive);
      const video = slide.querySelector("[data-media-video]");
      if (video) {
        if (isActive) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    });
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle("is-active", active);
      dot.setAttribute("aria-selected", String(active));
    });
    if (this.counterEl) {
      this.counterEl.textContent = String(index + 1).padStart(2, "0");
    }
  }

  #syncToVariant(variant) {
    if (!variant?.featured_image) return;
    const id = String(variant.featured_image.id);
    const index = [...this.slides].findIndex((s) => s.dataset.imageId === id);
    if (index < 0) return;
    this.#goTo(index);
  }
}

customElements.define("product-gallery", ProductGallery);

export { ProductGallery };
